import { ReactNode, useMemo } from 'react';
import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
} from 'react-hook-form';
import {
	FormControl,
	FormHelperText,
	InputLabel,
	Stack,
	TextField,
	Autocomplete as MuiAutocomplete,
} from '@mui/material';
import { Option } from '../../../api/DTO/common.interface';

interface ExtendedOption extends Option {
	childrenList?: ExtendedOption[];
}

interface AppAutocompleteProps {
	passObjectValue?: boolean;
	options: ExtendedOption[];
	infoPopUp?: ReactNode;
	removeErrorPadding?: boolean;
	disabled?: boolean;
	handleChange?: (value: string | number | ExtendedOption | null) => void;
	formControlStyle?: React.CSSProperties;
	isFullWidth?: boolean;
	isFormHelperText?: boolean;
	optional?: boolean;
	label?: string;
	size?: 'small' | 'medium';
}

// 工具函数：拍平父子结构
const flattenOptions = (options: ExtendedOption[] = []): ExtendedOption[] => {
	const result: ExtendedOption[] = [];
	for (const option of options) {
		// 如果有 childrenList，则父项不可选（保留用于显示分组）
		if (option.childrenList && option.childrenList.length > 0) {
			result.push({
				...option,
				_disabledAsParent: true, // 自定义标记
			});
			result.push(...flattenOptions(option.childrenList));
		} else {
			result.push(option);
		}
	}
	return result;
};

export default function AppAutoCompleteMenu<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	passObjectValue,
	label,
	options,
	handleChange,
	formControlStyle,
	isFullWidth = true,
	isFormHelperText = true,
	optional = false,
	...props
}: AppAutocompleteProps & UseControllerProps<TFieldValues, TName>) {
	const { field, fieldState } = useController(props);
	const errorMessage = fieldState.error?.message;

	// 拍平选项
	const flattenedOptions = useMemo(() => {
		const base = flattenOptions(options);
		if (optional) {
			// 插入空选项（可选）
			return [{ id: '', name: 'None', _isNoneOption: true }, ...base];
		}
		return base;
	}, [options, optional]);

	// 判断是否是父级（不可选）
	const isParentOption = (option: ExtendedOption) => {
		return (option as any)._disabledAsParent === true;
	};

	// 判断是否是 "None" 选项
	const isNoneOption = (option: ExtendedOption) => {
		return (option as any)._isNoneOption === true;
	};

	// 当前选中项
	const selectedOption = useMemo(() => {
		if (field.value == null || field.value === '') return null;
		if (
			passObjectValue &&
			typeof field.value === 'object' &&
			field.value !== null
		) {
			return field.value as ExtendedOption;
		}
		return (
			flattenedOptions.find(
				(opt) => !isNoneOption(opt) && opt.id === field.value
			) || null
		);
	}, [field.value, flattenedOptions, passObjectValue]);

	const handleOnChange = (_event: any, newValue: ExtendedOption | null) => {
		if (!newValue || isNoneOption(newValue)) {
			const finalValue = passObjectValue ? null : '';
			field.onChange(finalValue);
			handleChange?.(finalValue);
			return;
		}

		if (isParentOption(newValue)) {
			return;
		}

		const finalValue = passObjectValue ? newValue : newValue.id;
		field.onChange(finalValue);
		handleChange?.(finalValue);
	};

	return (
		<FormControl
			error={!!errorMessage}
			fullWidth={isFullWidth}
			size={props.size}
			style={formControlStyle}
		>
			{/* {label && <InputLabel shrink>{label}</InputLabel>} */}
			<MuiAutocomplete
				disabled={props.disabled}
				options={flattenedOptions}
				getOptionLabel={(option) => {
					if (isNoneOption(option)) return 'None';
					return option.name || '';
				}}
				getOptionDisabled={(option) => {
					if (isNoneOption(option)) return 'None';
					return option._disabledAsParent;
				}}
				isOptionEqualToValue={(option, value) =>
					option.id === value.id && !isParentOption(option)
				}
				renderOption={(renderProps, option) => {
					const isParent = isParentOption(option);
					const isNone = isNoneOption(option);
					return (
						<li
							{...renderProps}
							key={option.id}
							style={{
								...renderProps.style,
								paddingLeft: isParent ? 8 : 16,
								fontWeight: isParent ? 'bold' : 'normal',
								color: isParent ? '#999' : 'inherit',
								cursor: isParent ? 'default' : 'pointer',
							}}
						>
							<Stack direction='row' alignItems='center'>
								{option.icon && option.icon}
								&nbsp;{option.name}
							</Stack>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						error={!!errorMessage}
						helperText={undefined}
						size={props.size}
						label={label}
					/>
				)}
				value={selectedOption}
				onChange={handleOnChange}
				clearOnEscape
				handleHomeEndKeys
				selectOnFocus
				autoHighlight
				freeSolo={false}
				includeInputInList={false}
				disableCloseOnSelect={false}
			/>
			{isFormHelperText && <FormHelperText>{errorMessage}</FormHelperText>}
		</FormControl>
	);
}
